import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { DashExtensionUpsellSurface } from "@mirage/analytics/events/enums/dash_extension_upsell_surface";
import { PAP_Shown_DashExtensionUpsell } from "@mirage/analytics/events/types/shown_dash_extension_upsell";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import i18n from "@mirage/translations";
import { useEffect } from "react";
import styles from "./ExtensionUpsell.module.css";
import { ExtensionUpsellButton } from "./ExtensionUpsellButton";

const LIGHT_VIDEO_SRC =
  "https://assets.dropbox.com/images/dashweb/onboarding-extension-upsell-light.mp4";
const DARK_VIDEO_SRC =
  "https://assets.dropbox.com/images/dashweb/onboarding-extension-upsell-dark.mp4";

const LIGHT_STATIC_IMAGE_WEBP_SRC =
  "https://assets.dropbox.com/images/dashweb/onboarding-extension-upsell-light-static.webp";
const DARK_STATIC_IMAGE_WEBP_SRC =
  "https://assets.dropbox.com/images/dashweb/onboarding-extension-upsell-dark-static.webp";

const LIGHT_STATIC_IMAGE_PNG_SRC =
  "https://assets.dropbox.com/images/dashweb/onboarding-extension-upsell-light-static.png";
const DARK_STATIC_IMAGE_PNG_SRC =
  "https://assets.dropbox.com/images/dashweb/onboarding-extension-upsell-dark-static.png";

interface ExtensionUpsellProps {
  loggingSurface?: DashExtensionUpsellSurface;
  showExtensionUpsellButton?: boolean;
  useVideoAsset?: boolean;
  onExtensionUpsellButtonClick?: () => void;
}

export const ExtensionUpsell = ({
  loggingSurface,
  showExtensionUpsellButton = true,
  useVideoAsset = true,
  onExtensionUpsellButtonClick = () => {},
}: ExtensionUpsellProps) => {
  const isDarkMode = useTheme();
  const { reportPapEvent } = useMirageAnalyticsContext();

  useEffect(() => {
    reportPapEvent(
      PAP_Shown_DashExtensionUpsell({
        dashActionSurface: "setup",
        featureLine: "extension_upsell",
      }),
    );
  }, [reportPapEvent]);

  return (
    <div className={styles.extensionUpsellContainer}>
      {showExtensionUpsellButton && (
        <ExtensionUpsellButton
          surface={loggingSurface}
          uxaElementId={createUxaElementId(
            "team_onboarding_extension_upsell_link",
            {},
          )}
          onOpenExtensionLinkForCurrentBrowser={onExtensionUpsellButtonClick}
        />
      )}
      {useVideoAsset ? (
        <video className={styles.extensionUpsellAsset} autoPlay muted loop>
          <source
            src={isDarkMode ? DARK_VIDEO_SRC : LIGHT_VIDEO_SRC}
            type="video/mp4"
          />
        </video>
      ) : (
        <picture>
          <source
            srcSet={
              isDarkMode
                ? DARK_STATIC_IMAGE_WEBP_SRC
                : LIGHT_STATIC_IMAGE_WEBP_SRC
            }
            type="image/webp"
          />
          {/* Fallback to PNG for browsers that don't support webp */}
          <img
            src={
              isDarkMode
                ? DARK_STATIC_IMAGE_PNG_SRC
                : LIGHT_STATIC_IMAGE_PNG_SRC
            }
            className={styles.extensionUpsellAsset}
            alt={i18n.t("team_onboarding_extension_upsell_image_alt_text")}
          />
        </picture>
      )}
    </div>
  );
};
